'use client';

import {useEffect, useMemo, useState} from 'react';
import {useSearchParams} from 'next/navigation';
import {useAuth} from 'iq-auth';
import {UserGroup} from 'iq-auth/types';
import {BookOpen, CheckCircle} from 'lucide-react';
import Link from 'next/link';
import {FRONTEND_URL, theLoopReturnUrl} from 'config';
import {SubmissionSource} from 'iq-interview_experiences/types';

function SuccessMessage({showUnlock}: {showUnlock: boolean}) {
  if (showUnlock) {
    return (
      <div className='flex flex-col gap-2'>
        <h2 className='text-xl font-bold text-iq-black'>You&apos;ve unlocked full access!</h2>
        <p className='text-sm text-iq-grey-medium max-w-xs'>
          Thanks for sharing your experience. You now have complete access to this interview guide,
          including all questions and insights.
        </p>
      </div>
    );
  }
  return (
    <div className='flex flex-col gap-2'>
      <h2 className='text-xl font-bold text-iq-black'>Thanks for sharing!</h2>
      <p className='text-sm text-iq-grey-medium max-w-xs'>
        Your experience helps other candidates prepare. We&apos;ll review it before publishing.
      </p>
    </div>
  );
}

function returnUrlFromSource(source: string | null): string | null {
  if (source === SubmissionSource.TheLoop) return theLoopReturnUrl();
  if (source === SubmissionSource.UserDashboard) return `${FRONTEND_URL}/dashboard/my-interviews`;
  return null;
}

function ctaLabelFromSource(source: string | null, guideSlug?: string, showUnlock?: boolean): string {
  if (source === SubmissionSource.TheLoop) return 'Back to The Loop';
  if (source === SubmissionSource.UserDashboard) return 'Back to my interviews';
  if (showUnlock) return 'View full guide';
  if (guideSlug) return 'Back to guide';
  return 'Browse interview guides';
}

const ctaClassName =
  'px-6 py-3 bg-iq-red text-white text-sm font-semibold rounded-lg hover:opacity-90 transition-opacity';

function SuccessCta({
  href,
  label,
  onDismiss,
  canDismiss,
}: {
  href: string;
  label: string;
  onDismiss?: () => void;
  canDismiss: boolean;
}) {
  // In a modal the CTA target is the page already underneath the modal, so a
  // Link would be a no-op. Dismiss the modal instead (it reloads to reveal the
  // unlocked guide). External destinations (returnUrl) still navigate.
  if (canDismiss && onDismiss) {
    return (
      <button type='button' onClick={onDismiss} className={ctaClassName}>
        {label}
      </button>
    );
  }
  return (
    <Link href={href} className={ctaClassName}>
      {label}
    </Link>
  );
}

export function FlowSuccess({guideSlug, onDismiss}: {guideSlug?: string; onDismiss?: () => void}) {
  const {user} = useAuth();
  const source = useSearchParams().get('source');
  const returnUrl = returnUrlFromSource(source);
  const [resolvedReturnUrl, setResolvedReturnUrl] = useState<string>(returnUrl ?? '/');
  const isPremium =
    user.loggedIn &&
    (user.user_group_id === UserGroup.Premium || user.user_group_id === UserGroup.Admin);
  const showUnlock = !!guideSlug && !isPremium;

  useEffect(() => {
    if (!returnUrl || !user.loggedIn || source !== SubmissionSource.TheLoop) return;
    async function resolveUrl(email: string, fallback: string) {
      try {
        const res = await fetch('/api/sso/the-loop-token', {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({email}),
        });
        const data = res.ok ? ((await res.json()) as {sso_token?: string}) : {};
        const url = data.sso_token ? theLoopReturnUrl(data.sso_token) : fallback;
        console.log('return url:', url);
        setResolvedReturnUrl(url);
      } catch {
        console.log('return url:', fallback);
      }
    }
    void resolveUrl(user.email, returnUrl);
  }, [returnUrl, source, user]);

  const ctaHref = useMemo(() => {
    if (returnUrl) return resolvedReturnUrl;
    if (guideSlug) return `/guides/${guideSlug}`;
    return '/guides';
  }, [returnUrl, guideSlug, resolvedReturnUrl]);

  const ctaLabel = useMemo(
    () => ctaLabelFromSource(source, guideSlug, showUnlock),
    [source, guideSlug, showUnlock],
  );

  return (
    <div className='flex flex-col items-center justify-center text-center gap-6 py-16'>
      <div className='relative'>
        <div className='w-20 h-20 rounded-full bg-amber-50 flex items-center justify-center'>
          <BookOpen className='w-10 h-10 text-iq-yellow' />
        </div>
        <div className='absolute -top-1 -right-1 w-7 h-7 rounded-full bg-green-100 flex items-center justify-center'>
          <CheckCircle className='w-5 h-5 text-green-600' />
        </div>
      </div>
      <SuccessMessage showUnlock={showUnlock} />
      <SuccessCta
        href={ctaHref}
        label={ctaLabel}
        onDismiss={onDismiss}
        canDismiss={!returnUrl}
      />
    </div>
  );
}
